import type {
  BuyerIntent,
  MerchantQuote,
  PolicyCheck,
  PolicyDecision,
  SupportedCurrency,
} from "../types.js";
import { canonicalJson, normalizeText, randomNonce, sha256 } from "./canonical.js";

const CURRENCIES: SupportedCurrency[] = ["KES", "NGN", "BRL", "COP", "USD"];
const ADDRESS_PATTERN = /^0x[a-fA-F0-9]{40}$/;

export function normalizeIntent(
  candidate: Partial<BuyerIntent>,
  instruction: string,
): BuyerIntent {
  const fallback = fallbackIntent(instruction);
  const localCurrency = CURRENCIES.includes(candidate.localCurrency as SupportedCurrency)
    ? (candidate.localCurrency as SupportedCurrency)
    : fallback.localCurrency;
  return {
    expectedItem: cleanText(candidate.expectedItem, fallback.expectedItem, 100),
    quantity: clampInteger(candidate.quantity, fallback.quantity, 1, 100),
    maxLocalAmount: clampNumber(
      candidate.maxLocalAmount,
      fallback.maxLocalAmount,
      0.01,
      10_000_000,
    ),
    localCurrency,
    deadlineMinutes: clampInteger(candidate.deadlineMinutes, 15, 1, 1440),
    requireFulfillmentProof:
      candidate.requireFulfillmentProof === false
        ? false
        : fallback.requireFulfillmentProof,
    confidence: clampNumber(candidate.confidence, 0.55, 0, 1),
  };
}

export function fallbackIntent(instruction: string): BuyerIntent {
  const amountMatch = instruction.match(
    /\b(KES|NGN|BRL|COP|USD)\s*([0-9][0-9,]*(?:\.[0-9]+)?)/i,
  );
  const reverseAmountMatch = instruction.match(
    /\b([0-9][0-9,]*(?:\.[0-9]+)?)\s*(KES|NGN|BRL|COP|USD)\b/i,
  );
  const forItemMatch = instruction.match(
    /\bfor\s+(one|two|three|four|five|six|seven|eight|nine|ten|\d{1,3})\s+([^,.]{2,80})/iu,
  );
  const quantityMatch = instruction.match(
    /\b(\d{1,3})\s+(?:x\s+)?([\p{L}\p{N}][^,.]{1,80})/iu,
  );
  const currency = (
    amountMatch?.[1] ??
    reverseAmountMatch?.[2] ??
    "KES"
  ).toUpperCase() as SupportedCurrency;
  const amount = Number(
    (amountMatch?.[2] ?? reverseAmountMatch?.[1] ?? "2000").replaceAll(",", ""),
  );
  const item =
    (forItemMatch?.[2] ?? quantityMatch?.[2])
      ?.replace(/\b(?:before|by|with|and|for)\b.*$/i, "")
      .trim() || "merchant order";
  const quantity = quantityFromText(forItemMatch?.[1] ?? quantityMatch?.[1]);

  return {
    expectedItem: item,
    quantity,
    maxLocalAmount: Number.isFinite(amount) ? amount : 2000,
    localCurrency: CURRENCIES.includes(currency) ? currency : "KES",
    deadlineMinutes: 15,
    requireFulfillmentProof: true,
    confidence: 0.55,
  };
}

export async function evaluateQuote(
  intent: BuyerIntent,
  quote: MerchantQuote,
  now = new Date(),
  nonce = randomNonce(),
): Promise<PolicyDecision> {
  const checks: PolicyCheck[] = [];
  const amountPassed = quote.localAmount <= intent.maxLocalAmount;
  checks.push({
    code: amountPassed ? "amount_within_limit" : "blocked_amount",
    passed: amountPassed,
    label: "Budget ceiling",
    detail: amountPassed
      ? `${quote.localAmount.toLocaleString()} ${quote.localCurrency} is within the buyer's ${intent.maxLocalAmount.toLocaleString()} ${intent.localCurrency} ceiling.`
      : `Quote exceeds the buyer's limit by ${(quote.localAmount - intent.maxLocalAmount).toLocaleString()} ${quote.localCurrency}.`,
  });

  const currencyPassed = quote.localCurrency === intent.localCurrency;
  checks.push({
    code: currencyPassed ? "currency_matches" : "blocked_currency",
    passed: currencyPassed,
    label: "Currency",
    detail: currencyPassed
      ? `The quote is denominated in ${quote.localCurrency}.`
      : `Expected ${intent.localCurrency}, received ${quote.localCurrency}.`,
  });

  const expectedWords = new Set(tokens(intent.expectedItem));
  const quoteWords = new Set(tokens(quote.item));
  const overlap = [...expectedWords].filter(word => quoteWords.has(word));
  const descriptionMatches =
    intent.expectedItem === "merchant order" ||
    overlap.length >= Math.min(2, expectedWords.size) ||
    normalizeText(quote.item).includes(normalizeText(intent.expectedItem));
  const itemPassed = descriptionMatches && quote.quantity === intent.quantity;
  checks.push({
    code: itemPassed ? "item_matches" : "blocked_item",
    passed: itemPassed,
    label: "Order match",
    detail: itemPassed
      ? `${quote.quantity} × ${quote.item} matches the requested order.`
      : `Expected ${intent.quantity} × "${intent.expectedItem}", received ${quote.quantity} × "${quote.item}".`,
  });

  const expiresAt = Date.parse(quote.expiresAt);
  const quoteLive = Number.isFinite(expiresAt) && expiresAt > now.getTime() + 30_000;
  checks.push({
    code: quoteLive ? "quote_live" : "blocked_expired",
    passed: quoteLive,
    label: "Fresh quote",
    detail: quoteLive
      ? `The locked quote remains valid until ${quote.expiresAt}.`
      : "The quote is expired or too close to expiry.",
  });

  const fulfillmentText = normalizeText(quote.fulfillment);
  const fulfillmentTerms = [
    "handover",
    "pickup",
    "delivery",
    "delivered",
    "receipt",
    "code",
    "attest",
    "confirmation",
  ];
  const fulfillmentBound =
    !intent.requireFulfillmentProof ||
    (fulfillmentText.length >= 12 &&
      fulfillmentTerms.some(term => fulfillmentText.includes(term)));
  checks.push({
    code: fulfillmentBound ? "fulfillment_bound" : "blocked_fulfillment",
    passed: fulfillmentBound,
    label: "Fulfillment evidence",
    detail: fulfillmentBound
      ? quote.fulfillment
      : "The merchant has not committed to verifiable fulfillment evidence.",
  });

  const merchantValid = ADDRESS_PATTERN.test(quote.merchantAddress);
  checks.push({
    code: merchantValid ? "merchant_address_valid" : "blocked_merchant",
    passed: merchantValid,
    label: "Merchant destination",
    detail: merchantValid
      ? "The destination is a valid EVM address and is bound into the checkout hash."
      : "The merchant payment destination is invalid.",
  });

  const stableAmountSane =
    Number.isFinite(quote.stableAmount) &&
    quote.stableAmount > 0 &&
    quote.stableAmount <= 10_000;
  checks.push({
    code: stableAmountSane ? "stable_amount_sane" : "blocked_stable_amount",
    passed: stableAmountSane,
    label: "Stablecoin amount",
    detail: stableAmountSane
      ? `${quote.stableAmount.toFixed(2)} ${quote.stableSymbol} is locked at the displayed rate.`
      : "The stablecoin amount is missing or outside the safety range.",
  });

  const evaluatedAt = now.toISOString();
  const intentHash = await sha256({ ...intent, instructionVersion: 1 });
  const quoteHash = await sha256({ ...quote, quoteVersion: 1 });
  const checkoutHash = await sha256({
    intentHash,
    quoteHash,
    nonce,
    evaluatedAt,
    decisionVersion: 1,
  });

  return {
    allowed: checks.every(check => check.passed),
    checks,
    intentHash,
    quoteHash,
    checkoutHash,
    nonce,
    evaluatedAt,
  };
}

export async function decisionFingerprint(decision: PolicyDecision): Promise<string> {
  return sha256(canonicalJson(decision));
}

function cleanText(value: unknown, fallback: string, maxLength: number): string {
  if (typeof value !== "string") return fallback;
  const clean = value.replace(/\s+/g, " ").trim();
  return clean ? clean.slice(0, maxLength) : fallback;
}

function clampNumber(
  value: unknown,
  fallback: number,
  minimum: number,
  maximum: number,
): number {
  const number = typeof value === "number" ? value : Number(value);
  if (!Number.isFinite(number)) return fallback;
  return Math.min(maximum, Math.max(minimum, number));
}

function clampInteger(
  value: unknown,
  fallback: number,
  minimum: number,
  maximum: number,
): number {
  return Math.round(clampNumber(value, fallback, minimum, maximum));
}

function quantityFromText(value: string | undefined): number {
  if (!value) return 1;
  const words: Record<string, number> = {
    one: 1,
    two: 2,
    three: 3,
    four: 4,
    five: 5,
    six: 6,
    seven: 7,
    eight: 8,
    nine: 9,
    ten: 10,
  };
  return words[value.toLowerCase()] ?? clampInteger(value, 1, 1, 100);
}

function tokens(value: string): string[] {
  return normalizeText(value)
    .split(" ")
    .filter(Boolean)
    .map(token => (token.length > 3 && token.endsWith("s") ? token.slice(0, -1) : token));
}
